import type { ChartType } from '../../models/chart.types'
import type { ReportRow } from '../../models/report.types'

import { buildTimeSeriesChartOptions } from './buildTimeSeriesChartOptions'
import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import {
  type CategorySlot,
  categoryAxisLabels,
  categorySlots,
  combinedCategory,
  valuesByCategoryOccurrence,
} from './chartCategoryAxis'
import { groupRows } from './chartData'
import {
  buildChartMetricAxes,
  buildChartTooltip,
  buildChartTooltipChangeBadge,
  chartColumn,
  chartFieldLabel,
  formatChartValue,
  isNegativeMetric,
} from './chartPresentation'
import {
  expandChartBreakdownRows,
  formatChartTimeLabel,
  normalizeChartTimeValue,
} from './chartTime'
import { compareSeries } from './compareSeries'
import { firstField, manyFields, type ResolvedChartFields } from './resolveChartFields'

type CartesianType = Extract<
  ChartType,
  | 'line'
  | 'area'
  | 'stacked_area'
  | 'multi_line'
  | 'compare_column'
  | 'compare_bar'
  | 'column'
  | 'bar'
  | 'stacked_column'
  | 'stacked_bar'
  | 'combination'
>

const TIME_TYPES: readonly CartesianType[] = ['line', 'area', 'stacked_area', 'multi_line']

/** Hard ceiling on categories in the column/bar families. The catalog's `maxLimit` can only lower
 *  it: a larger or unbounded (`null`) backend limit still renders at most this many bars. */
const MAX_CATEGORY_LIMIT = 10
/** Renderer default for time-grouped series without resolved catalog presentation. */
const DEFAULT_TIME_GROUP_LIMIT = 5

function isCompareType(type: CartesianType): boolean {
  return type === 'compare_column' || type === 'compare_bar'
}

function baseSeriesType(type: CartesianType): 'line' | 'area' | 'column' | 'bar' {
  if (type === 'area' || type === 'stacked_area') return 'area'
  if (type === 'bar' || type === 'stacked_bar' || type === 'compare_bar') return 'bar'
  if (type === 'line' || type === 'multi_line') return 'line'
  return 'column'
}

function seriesGroups(
  rows: readonly ReportRow[],
  fields: readonly string[],
): Map<string, ReportRow[]> {
  if (fields.length === 0) return groupRows(rows, undefined)
  const groups = new Map<string, ReportRow[]>()
  for (const row of rows) {
    const key = combinedCategory(row, fields)
    const group = groups.get(key) ?? []
    group.push(row)
    groups.set(key, group)
  }
  return groups
}

function fieldsFor(
  type: CartesianType,
  fields: ResolvedChartFields,
): { axis: string[]; series: string[]; metrics: string[]; overlay: string[] } | null {
  const dimensions = manyFields(fields, ['dimensions'])
  const metrics = manyFields(fields, ['metrics'])
  if (type === 'multi_line') {
    const time = firstField(fields, ['time_dimension'])
    const dimension = firstField(fields, ['dimension'])
    const metric = firstField(fields, ['metric'])
    return time && dimension && metric
      ? { axis: [time], series: [dimension], metrics: [metric], overlay: [] }
      : null
  }

  if (TIME_TYPES.includes(type)) {
    const time = firstField(fields, ['time_dimension', 'dimension', 'dimensions'])
    if (!time || metrics.length === 0) return null
    return {
      axis: [time],
      series: dimensions.filter((field) => field !== time),
      metrics,
      overlay: [],
    }
  }

  const singleDimension = firstField(fields, ['dimension'])
  const timeDimension = firstField(fields, ['time_dimension'])
  const explicitSeries = [
    ...manyFields(fields, ['series_dimension', 'group_dimension']),
    ...(timeDimension ? [timeDimension] : []),
  ]
  const categoryFields =
    dimensions.length > 0
      ? dimensions
      : [...(singleDimension ? [singleDimension] : []), ...explicitSeries]
  if (categoryFields.length === 0 || metrics.length === 0) return null
  const axis = categoryFields.filter((field) => !explicitSeries.includes(field))
  return {
    axis: axis.length > 0 ? axis : categoryFields.slice(0, 1),
    series: explicitSeries.filter((field) => categoryFields.includes(field)),
    metrics,
    overlay: fields.role('line_metric').slice(0, 1),
  }
}

function buildSeries(
  input: BuildCategoricalChartOptionsInput,
  type: CartesianType,
  axisFields: readonly string[],
  groupingFields: readonly string[],
  metrics: readonly string[],
  slots: readonly CategorySlot[],
  overlay: readonly string[],
  metricAxisIndex: ReadonlyMap<string, number>,
): NonNullable<CategoricalChartOptions['series']> {
  const groups = seriesGroups(input.rows, groupingFields)
  const baseType = baseSeriesType(type)
  if (isCompareType(type)) return compareSeries(input, groups, axisFields, metrics, slots, baseType)
  const groupEntries = [...groups.entries()]
    .sort((left, right) => {
      const timeField = groupingFields.length === 1 ? groupingFields[0] : undefined
      if (!timeField || !input.timeDimensions?.includes(timeField)) return 0
      return (
        (normalizeChartTimeValue(left[1][0]?.[timeField], timeField, left[1][0]) ?? 0) -
        (normalizeChartTimeValue(right[1][0]?.[timeField], timeField, right[1][0]) ?? 0)
      )
    })
    .slice(
      0,
      groupingFields.some((field) => input.timeDimensions?.includes(field))
        ? (input.presentation?.maxSeries ?? DEFAULT_TIME_GROUP_LIMIT)
        : undefined,
    )
  const primary = groupEntries.flatMap(([group, rows]) =>
    metrics.map((metric, metricIndex) => {
      const seriesType = type === 'combination' && metricIndex > 0 ? 'line' : baseType
      const timeField = groupingFields.length === 1 ? groupingFields[0] : undefined
      const timeValue = timeField
        ? normalizeChartTimeValue(rows[0]?.[timeField], timeField, rows[0])
        : null
      const isTimeGroup = Boolean(timeField && input.timeDimensions?.includes(timeField))
      const presentedGroup =
        isTimeGroup && timeField && timeValue !== null
          ? formatChartTimeLabel(timeValue, timeField)
          : group
      return {
        type: seriesType,
        name: presentedGroup
          ? isTimeGroup && metrics.length === 1
            ? presentedGroup
            : `${chartFieldLabel(input, metric)} · ${presentedGroup}`
          : chartFieldLabel(input, metric),
        data: valuesByCategoryOccurrence(rows, axisFields, metric, slots),
        ...((metricAxisIndex.get(metric) ?? 0) > 0 ? { yAxis: metricAxisIndex.get(metric) } : {}),
        ...(input.columns || input.formatValue ? { custom: { metric } } : {}),
      }
    }),
  )
  const secondary = groupEntries.flatMap(([group, rows]) =>
    overlay.map((metric) => {
      return {
        type: 'line' as const,
        name: group
          ? `${chartFieldLabel(input, metric)} · ${group}`
          : chartFieldLabel(input, metric),
        data: valuesByCategoryOccurrence(rows, axisFields, metric, slots),
        yAxis: metricAxisIndex.get(metric) ?? 1,
        ...(input.columns || input.formatValue ? { custom: { metric } } : {}),
      }
    }),
  )
  return [...primary, ...secondary]
}

export function buildCartesianChartOptions(
  input: BuildCategoricalChartOptionsInput,
  type: CartesianType,
  fields: ResolvedChartFields,
): CategoricalChartOptions | null {
  if (type === 'line' || type === 'area' || type === 'stacked_area' || type === 'multi_line') {
    return buildTimeSeriesChartOptions(input, type, fields)
  }
  const resolved = fieldsFor(type, fields)
  if (!resolved) return null

  const rows = expandChartBreakdownRows(input.rows)
  const categoryInput = { ...input, rows }

  const mediaDimensions = (input.mediaDimensions ?? []).filter((field) =>
    resolved.axis.includes(field),
  )
  const slots = categorySlots(rows, resolved.axis, resolved.series, mediaDimensions).slice(
    0,
    Math.min(input.presentation?.maxLimit ?? MAX_CATEGORY_LIMIT, MAX_CATEGORY_LIMIT),
  )
  const categories = slots.map(({ label }) => label)
  const singleCompare = isCompareType(type) && resolved.metrics.length === 1
  const compareActualMetric = singleCompare ? resolved.metrics[0] : undefined
  const compareActualValues = compareActualMetric
    ? valuesByCategoryOccurrence(rows, resolved.axis, compareActualMetric, slots).map((value) =>
        formatChartValue(categoryInput, value, compareActualMetric),
      )
    : undefined
  const labels = categoryAxisLabels(slots, compareActualValues)
  const allMetrics = [...new Set([...resolved.metrics, ...resolved.overlay])]
  const { yAxis, metricAxisIndex } = buildChartMetricAxes(input, allMetrics)

  const stacking = type.startsWith('stacked_') ? 'normal' : undefined
  const metricsCount = Math.max(1, resolved.metrics.length)
  const categoryCount = Math.max(1, slots.length)
  const barHeight = Math.max(440, categoryCount * (34 + metricsCount * 16) + 140)
  return {
    chart: {
      type: baseSeriesType(type),
      ...(baseSeriesType(type) === 'bar' ? { height: barHeight, spacingLeft: 8 } : {}),
    },
    title: {
      text: resolved.axis.map((field) => chartFieldLabel(input, field)).join(' / '),
      style: { fontSize: '12px', fontWeight: '700' },
    },
    xAxis: {
      categories,
      crosshair: true,
      type: 'category',
      labels,
      // Column/bar families (plain, stacked, and compare) sit directly on the value gridline;
      // the solid axis baseline reads as a stray border beyond the bars themselves.
      ...(type !== 'combination' &&
      (baseSeriesType(type) === 'column' || baseSeriesType(type) === 'bar')
        ? { lineWidth: 0, tickWidth: 0 }
        : {}),
    },
    yAxis,
    legend: {
      itemStyle: { color: 'var(--ui-chart-legend)', fontSize: '12px', fontWeight: '400' },
      useHTML: true,
      ...(baseSeriesType(type) === 'bar' ? { verticalAlign: 'top' } : {}),
    },
    tooltip: {
      shared: true,
      outside: true,
      useHTML: true,
      formatter() {
        const context = this as unknown as {
          category: unknown
          points?: Array<{
            color?: unknown
            y?: unknown
            series: {
              name: string
              userOptions?: { custom?: { metric?: string; compareRole?: 'actual' | 'previous' } }
            }
          }>
        }
        const rows = (context.points ?? []).map((point) => {
          const metric = point.series.userOptions?.custom?.metric ?? resolved.metrics[0]!
          return {
            color: point.color,
            label: point.series.name,
            value: formatChartValue(input, point.y, metric),
          }
        })
        let changeBadgeHtml: string | undefined
        if (singleCompare) {
          const metricField = resolved.metrics[0] ?? ''
          const column = chartColumn(input, metricField)
          const isNegative = isNegativeMetric(metricField, column)
          const actualPoint = context.points?.find(
            (point) =>
              point.series.userOptions?.custom?.compareRole === 'actual' ||
              point.series.name === 'Actual',
          )
          const previousPoint = context.points?.find(
            (point) =>
              point.series.userOptions?.custom?.compareRole === 'previous' ||
              point.series.name === 'Previous',
          )
          const actualValue = typeof actualPoint?.y === 'number' ? actualPoint.y : null
          const previousValue = typeof previousPoint?.y === 'number' ? previousPoint.y : null
          changeBadgeHtml = buildChartTooltipChangeBadge(actualValue, previousValue, isNegative)
        }
        return buildChartTooltip(context.category, rows, changeBadgeHtml)
      },
    },
    series: buildSeries(
      categoryInput,
      type,
      resolved.axis,
      resolved.series,
      resolved.metrics,
      slots,
      resolved.overlay,
      metricAxisIndex,
    ),
    plotOptions: {
      series: { borderWidth: 0 },
      ...(baseSeriesType(type) === 'column'
        ? {
            column: {
              maxPointWidth: 40,
              borderRadius: 4,
              ...(stacking ? { stacking } : {}),
            },
          }
        : {}),
      ...(baseSeriesType(type) === 'bar'
        ? {
            bar: {
              maxPointWidth: 40,
              borderRadius: 10,
              ...(stacking ? { stacking } : {}),
            },
          }
        : {}),
    },
    credits: { enabled: false },
  }
}
